import Sockudo from "@sockudo/client";
import { create } from 'zustand';
import { handleWebSocketEvent } from "../Helper/WebSocketEventHandler";
import { resolveRealtimeHost } from "../Helper/RegionHosts";
import { WebSocketEvent } from "../Models/WebSocketEventData";
import { useSessionStore } from "./Session";

// Realtime connection: self-hosted Sockudo (Pusher/Channels wire-compatible) via
// @sockudo/client. The host is resolved per connection (see resolveRealtimeHost):
// an explicit realtimeHost from the host SDK, else the sockets.* host of the
// region the session's apiUrl points at, else this build default. The app key +
// channel/event names are unchanged from the old Pusher setup. Env access must
// stay STATIC (process.env.REACT_APP_*).
const DEFAULT_SOCKUDO_HOST =
  process.env.REACT_APP_SOCKUDO_HOST || "sockets.gleap.io";

// Sockudo app key (public, safe to ship).
const APP_KEY = "29b0a09928856b262405";

// The live client field is kept named `pusher` to avoid touching the store shape
// and call sites (CommunicationManager).
type RealtimeClient = Sockudo;

interface WebSocketState {
  pusher?: RealtimeClient | null;
  pusherWasDisconnected: boolean;
  connect: (onReconnect: () => void) => void;
  disconnect: () => void;
}

export const useWebSocketStore = create<WebSocketState>()((set, get) => ({
  pusher: null,
  pusherWasDisconnected: false,
  connect: (onReconnect) => {
    const { session, sdkKey, apiUrl, realtimeHost } =
      useSessionStore.getState();
    if (!session || !sdkKey || !apiUrl) {
      return;
    }

    // Idempotent connect: Sockudo auto-connects on construction, so overwriting
    // `pusher` with a new instance without disconnecting the old one leaks a
    // second live socket (both stay subscribed to private-session-<gleapId>).
    // A repeated connect() — e.g. the chatbar's boot flow delivers two
    // widget-status-update(true) messages, each calling reconnect() — must
    // replace, not stack. Tear the existing connection down first.
    const existing = get().pusher;
    if (existing) {
      try {
        existing.disconnect();
      } catch (e) {}
      get().pusher = null;
    }

    const authHeaders = {
      "api-token": sdkKey,
      "gleap-id": session.gleapId,
      "gleap-hash": session.gleapHash,
    };

    // Authenticate the user/session against the existing session-auth endpoints,
    // now validated by Sockudo (same mirrored secret).
    get().pusher = new Sockudo(APP_KEY, {
      wsHost: resolveRealtimeHost(realtimeHost, apiUrl, DEFAULT_SOCKUDO_HOST),
      wssPort: 443,
      forceTLS: true,
      enabledTransports: ["ws", "wss"],
      protocolVersion: 2,
      connectionRecovery: true,
      userAuthentication: {
        endpoint: `${apiUrl}/v3/users/me/session-auth`,
        transport: "ajax",
        headers: authHeaders,
      },
      channelAuthorization: {
        endpoint: `${apiUrl}/v3/users/me/session-channel-auth`,
        transport: "ajax",
        headers: authHeaders,
      },
    });

    const pusher = get().pusher;
    if (pusher) {
      pusher.connection.bind('state_change', (state: any) => {
        // Temporary failure of the Channels connection will cause: connected -> connecting -> connected
        // If an internet connection disappears: connected -> connecting -> unavailable (after ~ 30s
        if (
          state.current === 'unavailable' ||
          (state.previous === 'connected' && state.current === 'connecting')
        ) {
          set({
            pusherWasDisconnected: true,
          });
        } else if (state.current === 'connected' && get().pusherWasDisconnected) {
          set({
            pusherWasDisconnected: false,
          });

          if (onReconnect) {
            onReconnect();
          }
        }
      });

      // Sockudo V2 connection recovery (server >= 4.x): if a channel can't be
      // replayed after a reconnect, fall back to a full data reload so the UI
      // is never left stale.
      pusher.bind('sockudo:resume_failed', () => {
        if (onReconnect) {
          onReconnect();
        }
      });
    }

    try {
      get().pusher?.signin();

      // Subscribe to channels
      const privateSessionChannel = get().pusher?.subscribe(
        `private-session-${session.gleapId}`
      );

      // Listen to events
      const eventsToSubscribe = Object.keys(WebSocketEvent);

      for (let i = 0; i < eventsToSubscribe.length; i++) {
        const event =
          WebSocketEvent[eventsToSubscribe[i] as keyof typeof WebSocketEvent];

        privateSessionChannel?.bind(event, (data: any) => {
          handleWebSocketEvent(event, data);
        });
      }
    } catch (exp) { }
  },
  disconnect: () => {
    const { session } = useSessionStore.getState();

    if (!get().pusher || !session) {
      return;
    }

    get().pusher?.disconnect();
  },
}));
